import { cn } from '@/lib/utils';

/**
 * Live navigation icon. Colors via `currentColor` so the parent's text
 * color (and `transition-colors`) drives the active/inactive animation.
 *
 * The broadcast glyph is built from concentric stroked arcs — an outer pair,
 * an inner pair, and a centre dot — so they can animate as a radar. The arcs
 * use `vector-effect: non-scaling-stroke`, so scaling them changes only the
 * radius, not the line width.
 *
 * @param active - When `true`, plays a radar "ping": the inner arcs grow out
 *   onto the outer arcs' radius while the outer arcs drift out and fade, and a
 *   new pair (`.nav-live-spawn`) grows from the centre onto the inner radius.
 *   Constant stroke width means the end geometry matches the resting glyph, so
 *   it settles seamlessly. Keyframes in `index.css`, keyed on `data-active`.
 */
export function LiveIcon({ active, className, ...props }: React.SVGProps<SVGSVGElement> & { active?: boolean }) {
  return (
    <svg
      viewBox="0 0 33 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('nav-icon nav-icon--live', className)}
      data-active={active ? 'true' : undefined}
      {...props}
    >
      {/* Outer broadcast arcs (r=13) — drift outward and fade on ping. */}
      <path
        className="nav-live-outer"
        d="M25.392 7.208 A13 13 0 0 1 25.392 25.592 M7.008 7.208 A13 13 0 0 0 7.008 25.592"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* Inner arcs (r=7) — grow out onto the outer radius on ping. */}
      <path
        className="nav-live-inner"
        d="M21.15 11.45 A7 7 0 0 1 21.15 21.35 M11.25 11.45 A7 7 0 0 0 11.25 21.35"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* New arcs spawning from the centre, growing onto the inner radius. */}
      <path
        className="nav-live-spawn"
        d="M21.15 11.45 A7 7 0 0 1 21.15 21.35 M11.25 11.45 A7 7 0 0 0 11.25 21.35"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* Centre dot — static. */}
      <circle cx="16.2" cy="16.4" r="2.5" fill="currentColor" />
    </svg>
  );
}
